import { type ButtonHTMLAttributes, type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { useGetCommonStore } from '@/store';
import { Spinner } from '@/styles/common';
import { ScWheelButton } from './styled';

interface WheelButtonProps {
  invert: boolean;
  isSpinning: boolean;
  isLoading: boolean;
  isLocked: boolean;
  isComingSoon?: boolean;
}

export const WheelButton: FC<
  ButtonHTMLAttributes<HTMLButtonElement> & WheelButtonProps
> = ({
  invert,
  isSpinning,
  isLoading,
  isLocked,
  isComingSoon = false,
  ...props
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(['common', 'wheel']);
  const { blackColor, mainColor } = useTheme();
  const useCommonStore = useGetCommonStore();

  const isAuthenticated = useCommonStore.use.isAuthenticated();

  const buttonText = useMemo(() => {
    if (!isAuthenticated) return t('common:button.register');
    if (isComingSoon) return t('wheel:comingSoon');
    if (isLocked) return t('wheel:allSpinsUsed');

    return invert ? t('wheel:buySpin') : t('wheel:spin');
  }, [isAuthenticated, isComingSoon, isLocked, invert, t]);

  return (
    <ScWheelButton
      {...props}
      disabled={isSpinning || (isComingSoon && isAuthenticated)}
      isAuth={isAuthenticated}
      isComingSoon={isComingSoon}
      isLocked={isLocked}
      isSpinning={isSpinning}
      lng={language}
    >
      {isLoading ? (
        <Spinner background={blackColor} center color={mainColor} size={60} />
      ) : (
        <p className="message">{buttonText}</p>
      )}
    </ScWheelButton>
  );
};
